import { clsx } from 'clsx'
import { ChevronLeft, ChevronRight, ClipboardList } from 'lucide-react'
import { Fragment, useState } from 'react'
import { ApplicationDetailPanel } from '@/components/builder-applications/application-detail-panel'
import { LoadingPage } from '@/components/ui/loading'
import { StatusBadge } from '@/components/ui/status-badge'
import { useBuilderApplications } from '@/hooks/use-builder-applications'
import type { BuilderApplication } from '@/lib/types'

const PER_PAGE = 20

const STATUS_OPTIONS = ['all', 'pending', 'approved', 'denied'] as const
const TYPE_OPTIONS = ['all', 'student', 'startup'] as const

export function BuilderApplicationsPage(): React.JSX.Element {
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data, isLoading, error } = useBuilderApplications({
    page,
    per_page: PER_PAGE,
    status: statusFilter === 'all' ? undefined : statusFilter,
    type: typeFilter === 'all' ? undefined : typeFilter,
  })

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader />
        <div className="rounded-xl border border-danger/30 bg-surface-0 p-12 text-center">
          <p className="text-danger">Failed to load applications. Please try again.</p>
        </div>
      </div>
    )
  }

  const applications = data?.data ?? []
  const totalPages = data ? Math.ceil(data.total / data.per_page) : 1
  const showStart = data ? (data.page - 1) * data.per_page + 1 : 0
  const showEnd = data ? Math.min(data.page * data.per_page, data.total) : 0

  return (
    <div className="space-y-6">
      <PageHeader />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-admin-500 focus:outline-none"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value)
            setPage(1)
          }}
          className="rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary focus:border-admin-500 focus:outline-none"
        >
          {TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t === 'all' ? 'All Types' : t.charAt(0).toUpperCase() + t.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <LoadingPage message="Loading applications..." />
      ) : applications.length === 0 ? (
        <div className="flex items-center justify-center rounded-xl border border-surface-3 bg-surface-0 py-16">
          <p className="text-text-secondary">No applications found matching your filters.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-surface-3 bg-surface-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-surface-3 bg-surface-1">
                <tr>
                  {['Name', 'Email', 'Type', 'Company / School', 'Location', 'Status', 'Date'].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-2">
                {applications.map((app) => (
                  <Fragment key={app.id}>
                    <ApplicationRow
                      application={app}
                      isExpanded={expandedId === app.id}
                      onToggle={() => setExpandedId(expandedId === app.id ? null : app.id)}
                    />
                    {expandedId === app.id && (
                      <ApplicationDetailPanel
                        application={app}
                        onClose={() => setExpandedId(null)}
                      />
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data && (
            <div className="flex items-center justify-between border-t border-surface-3 px-4 py-3">
              <p className="text-sm text-text-secondary">
                Showing {showStart}-{showEnd} of {data.total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg p-1.5 text-text-secondary hover:bg-surface-2 disabled:opacity-40"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-text-secondary">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="rounded-lg p-1.5 text-text-secondary hover:bg-surface-2 disabled:opacity-40"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function PageHeader(): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <ClipboardList className="h-6 w-6 text-admin-500" />
      <h1 className="text-2xl font-semibold text-text-primary">Builder Applications</h1>
    </div>
  )
}

function ApplicationRow({
  application,
  isExpanded,
  onToggle,
}: {
  application: BuilderApplication
  isExpanded: boolean
  onToggle: () => void
}): React.JSX.Element {
  return (
    <tr
      className={clsx(
        'cursor-pointer transition-colors hover:bg-surface-1',
        isExpanded && 'bg-surface-1',
      )}
      onClick={onToggle}
    >
      <td className="whitespace-nowrap px-4 py-3 font-medium text-text-primary">
        {application.name}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-text-primary">{application.email}</td>
      <td className="whitespace-nowrap px-4 py-3">
        <StatusBadge status={application.type} />
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-text-primary">
        {application.company_or_school ?? '-'}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
        {application.location ?? '-'}
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <StatusBadge status={application.status} />
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
        {new Date(application.created_at).toLocaleDateString()}
      </td>
    </tr>
  )
}
